import type { CategoryResponseDto, LiveEventCardDto, LivePageResponseDto } from '@/lib/models';

/** The three Live-page card groups, in page order. */
export interface LiveGroups {
  closingSoon: LiveEventCardDto[];
  today: LiveEventCardDto[];
  thisWeek: LiveEventCardDto[];
}

/** Group keys in fixed render order (spec §4.4). */
export const LIVE_GROUP_KEYS = ['closingSoon', 'today', 'thisWeek'] as const;
export type LiveGroupKey = (typeof LIVE_GROUP_KEYS)[number];

/**
 * The snake sub-category codes (mirrors the api's
 * RECURRING_CRYPTO_SUBCATEGORIES in snake-market.constants.ts) — used to spot
 * the category pill that owns the Short Markets widget.
 */
const SNAKE_SUB_CATEGORY_CODES = new Set(['CRYPTO_5MIN', 'CRYPTO_20MIN', 'CRYPTO_2HOURS', 'CRYPTO_DAILY', 'CRYPTO_WEEKLY']);

/** Server payload → the three card groups (order preserved — pins come first from the server). */
export function toLiveGroups(data: LivePageResponseDto): LiveGroups {
  return {
    closingSoon: data.closingSoon.events,
    today: data.today.events,
    thisWeek: data.thisWeek.events,
  };
}

/**
 * Drop territory-hidden categories from every group (mirrors the lobby:
 * hidden codes are uppercase, cf. CategoryTabsContainer / useHiddenCategories).
 */
export function filterHiddenLiveGroups(groups: LiveGroups, hiddenCategories: string[]): LiveGroups {
  if (hiddenCategories.length === 0) return groups;
  const hidden = new Set(hiddenCategories);
  const keep = (e: LiveEventCardDto) => !hidden.has(e.categoryCode.toUpperCase());
  return {
    closingSoon: groups.closingSoon.filter(keep),
    today: groups.today.filter(keep),
    thisWeek: groups.thisWeek.filter(keep),
  };
}

/**
 * The header "N Live Now" count: only events actually in play (the red-badge
 * `isLive` set) — product amended the spec's all-eligible definition, which
 * read as false advertising over a page that's mostly This Week. Computed
 * BEFORE the category pill filter — selecting a pill never changes it (§9).
 * Snakes never count: they carry no in-play state.
 */
export function liveNowCount(visibleGroups: LiveGroups): number {
  return LIVE_GROUP_KEYS.reduce((sum, key) => sum + visibleGroups[key].filter((e) => e.isLive).length, 0);
}

/**
 * Every visible eligible event across the groups plus the short-markets
 * (snake) events — drives the all-empty state: a page with zero in-play
 * events but plenty upcoming still renders its groups.
 */
export function eligibleEventCount(visibleGroups: LiveGroups, shortMarketsCount: number): number {
  return visibleGroups.closingSoon.length + visibleGroups.today.length + visibleGroups.thisWeek.length + shortMarketsCount;
}

/**
 * Apply the category-pill filter: keep events in the selected category — or,
 * when a sub-category is selected, that exact sub-category. `null` category =
 * the "All" pill (no filter). Order is preserved (pins stay first).
 */
export function filterLiveGroupsByCategory(
  groups: LiveGroups,
  category: string | null,
  subCategory: string | null,
): LiveGroups {
  if (!category) return groups;
  const keep = (e: LiveEventCardDto) => e.categoryCode === category && (!subCategory || e.subCategoryCode === subCategory);
  return {
    closingSoon: groups.closingSoon.filter(keep),
    today: groups.today.filter(keep),
    thisWeek: groups.thisWeek.filter(keep),
  };
}

/**
 * Whether the Short Markets (snake) section shows under the active pill:
 * visible on "All" (no filter), or when the selected category owns the snake
 * sub-categories (crypto). Any other pill hides it — snakes carry no category
 * on their cards, so "filters all groups" (spec §4.2) hides them too.
 */
export function isShortMarketsVisible(category: string | null, categories: CategoryResponseDto[]): boolean {
  if (!category) return true;
  return findSnakeCategoryCode(categories) === category;
}

/** The code of the category that owns the snake (recurring crypto) sub-categories, or null. */
export function findSnakeCategoryCode(categories: CategoryResponseDto[]): string | null {
  return categories.find((c) => c.subCategories.some((s) => SNAKE_SUB_CATEGORY_CODES.has(s.code)))?.code ?? null;
}

/**
 * Category codes with at least one eligible event on the page — pills for
 * empty categories are hidden. The snake-owning category counts as present
 * while there are eligible short markets (the widget is its content).
 */
export function presentCategoryCodes(
  groups: LiveGroups,
  shortMarketsCount: number,
  categories: CategoryResponseDto[],
): Set<string> {
  const present = new Set<string>();
  for (const key of LIVE_GROUP_KEYS) for (const e of groups[key]) present.add(e.categoryCode);
  if (shortMarketsCount > 0) {
    const snakeCategory = findSnakeCategoryCode(categories);
    if (snakeCategory) present.add(snakeCategory);
  }
  return present;
}

/** Sub-category codes with at least one eligible event in the given category — empty sub-category chips hide. */
export function presentSubCategoryCodes(groups: LiveGroups, category: string): Set<string> {
  const present = new Set<string>();
  for (const key of LIVE_GROUP_KEYS) {
    for (const e of groups[key]) {
      if (e.categoryCode === category && e.subCategoryCode) present.add(e.subCategoryCode);
    }
  }
  return present;
}
